import React, { useState, useMemo } from 'react';
import { Smartphone, Monitor, Sun, Moon, Info, Check, Instagram } from 'lucide-react';

interface UserProfile {
  device: 'mobile' | 'desktop';
  time: 'day' | 'night';
}

interface Rule {
  id: string;
  label: string;
  conditions: {
    device?: 'mobile' | 'desktop';
    time?: 'day' | 'night';
  };
  destination: string;
  isFallback?: boolean;
}

const rules: Rule[] = [
  {
    id: 'mobile',
    label: 'Mobile',
    conditions: { device: 'mobile' },
    destination: 'm.bistro.com/menu',
  },
  {
    id: 'desktop-day',
    label: 'Desktop + Day',
    conditions: { device: 'desktop', time: 'day' },
    destination: 'bistro.com/lunch',
  },
  {
    id: 'fallback',
    label: 'Default',
    conditions: {},
    destination: 'instagram.com/thebistro',
    isFallback: true,
  },
];

function matchesRule(profile: UserProfile, rule: Rule): boolean {
  const { conditions } = rule;
  if (conditions.device && conditions.device !== profile.device) return false;
  if (conditions.time && conditions.time !== profile.time) return false;
  return true;
}

function getMatchedRuleIndex(profile: UserProfile): number {
  for (let i = 0; i < rules.length; i++) {
    if (matchesRule(profile, rules[i])) {
      return i;
    }
  }
  return rules.length - 1;
}

// Simple tooltip
function Tooltip({ content }: { content: string }) {
  const [show, setShow] = useState(false);

  return (
    <div className="relative inline-flex">
      <button
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        className="text-[--clinqr-text-muted] hover:text-[--clinqr-text-secondary] transition-colors"
      >
        <Info className="w-4 h-4" />
      </button>
      <div
        className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-[--clinqr-text-primary] text-white text-xs rounded-lg whitespace-nowrap z-50 shadow-xl transition-all duration-200 ${
          show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1 pointer-events-none'
        }`}
      >
        {content}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[--clinqr-text-primary]" />
      </div>
    </div>
  );
}

export function HowItWorks() {
  const [profile, setProfile] = useState<UserProfile>({
    device: 'mobile',
    time: 'day',
  });

  const matchedIndex = useMemo(() => getMatchedRuleIndex(profile), [profile]);
  const matchedRule = rules[matchedIndex];

  // Day/night theming
  const isNight = profile.time === 'night';

  return (
    <section
      className="relative overflow-hidden min-h-screen flex flex-col justify-center section-padding transition-colors duration-700"
      data-section="how-it-works"
      style={{
        backgroundColor: isNight ? '#101F1C' : '#F1F4F3',
      }}
    >
      {/* Ambient gradient - lime/navy tones */}
      <div
        className="absolute inset-0 transition-all duration-700 ease-out pointer-events-none"
        style={{
          background: isNight
            ? `radial-gradient(ellipse 60% 50% at 50% 50%, rgba(212, 255, 128, 0.05) 0%, transparent 70%)`
            : `radial-gradient(ellipse 60% 50% at 50% 50%, rgba(16, 31, 28, 0.02) 0%, transparent 70%)`,
        }}
      />

      {/* Night stars */}
      {isNight && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="absolute w-0.5 h-0.5 bg-[--clinqr-lime] rounded-full"
              style={{
                left: `${(i * 37) % 100}%`,
                top: `${(i * 23) % 100}%`,
                opacity: 0.2 + (i % 3) * 0.2,
              }}
            />
          ))}
        </div>
      )}

      <div className="relative container-page max-w-7xl">
        {/* Header */}
        <div className="text-center mb-16">
          <p className={`label mb-4 transition-colors duration-500 font-bold ${isNight ? 'text-[--clinqr-lime]' : 'text-[--clinqr-navy]'}`}>
            INTERACTIVE DEMO
          </p>
          <h2 className={`heading-1 mb-4 transition-colors duration-500 ${isNight ? 'text-[--clinqr-lime]' : 'text-[--clinqr-navy]'}`}>
            Dynamic Routing Demo
          </h2>
          <p className={`body-lg max-w-2xl mx-auto transition-colors duration-500 font-small ${isNight ? 'text-[--clinqr-lime] opacity-80' : 'text-[--clinqr-navy] opacity-80'}`}>
            One QR code, evaluated top-to-bottom. First matching rule wins.
          </p>
        </div>

        {/* Main Card */}
        <div
          className={`rounded-3xl border transition-all duration-500 ${
            isNight
              ? 'bg-white/5 border-white/10 shadow-2xl'
              : 'bg-white border-[--clinqr-navy]/10 shadow-2xl'
          }`}
          style={{ width: '80vw', maxWidth: '1400px', margin: '0 auto' }}
        >
          <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-[--clinqr-navy]/5">

            {/* Left: Controls */}
            <div className="p-8 lg:p-12">
              <p className={`label mb-10 transition-colors duration-500 font-bold ${isNight ? 'text-white/30' : 'text-[--clinqr-navy]/30'}`}>
                User Context
              </p>

              {/* Device */}
              <div className="mb-10">
                <div className="flex items-center gap-2 mb-5">
                  <span className={`text-sm font-bold uppercase tracking-wider transition-colors duration-500 ${isNight ? 'text-white' : 'text-[--clinqr-navy]'}`}>
                    Device
                  </span>
                  <Tooltip content="Full control: iOS, Android, browser, OS" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {(['mobile', 'desktop'] as const).map((device) => (
                    <button
                      key={device}
                      onClick={() => setProfile((p) => ({ ...p, device }))}
                      className={`flex items-center justify-center gap-3 py-4 px-6 rounded-2xl text-sm font-bold transition-all duration-300 border ${
                        profile.device === device
                          ? 'bg-[--clinqr-navy] text-[--clinqr-lime] shadow-xl scale-[1.02] border-transparent'
                          : isNight
                          ? 'bg-transparent text-white/60 hover:bg-white/20 border-white/30'
                          : 'bg-transparent text-[--clinqr-navy]/60 hover:bg-gray-100 border-[--clinqr-navy]/30'
                      }`}
                    >
                      {device === 'mobile' ? <Smartphone className="w-5 h-5" /> : <Monitor className="w-5 h-5" />}
                      {device.charAt(0).toUpperCase() + device.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Time */}
              <div>
                <div className="flex items-center gap-2 mb-5">
                  <span className={`text-sm font-bold uppercase tracking-wider transition-colors duration-500 ${isNight ? 'text-white' : 'text-[--clinqr-navy]'}`}>
                    Time of Day
                  </span>
                  <Tooltip content="Full control: Hours, days, dates, timezone" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {(['day', 'night'] as const).map((time) => (
                    <button
                      key={time}
                      onClick={() => setProfile((p) => ({ ...p, time }))}
                      className={`flex items-center justify-center gap-3 py-4 px-6 rounded-2xl text-sm font-bold transition-all duration-300 border ${
                        profile.time === time
                          ? 'bg-[--clinqr-navy] text-[--clinqr-lime] shadow-xl scale-[1.02] border-transparent'
                          : isNight
                          ? 'bg-transparent text-white/60 hover:bg-white/20 border-white/30'
                          : 'bg-transparent text-[--clinqr-navy]/60 hover:bg-gray-100 border-[--clinqr-navy]/30'
                      }`}
                    >
                      {time === 'day' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                      {time.charAt(0).toUpperCase() + time.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Flowchart */}
            <div className="p-8 lg:p-12 bg-[--clinqr-navy]/5 flex flex-col justify-center">
              {/* QR Code */}
              <div className="flex justify-center mb-8">
                <div className="relative">
                  {/* Pulse */}
                  <div className="absolute inset-0 rounded-2xl bg-[--clinqr-navy]/10 animate-[ping_2s_ease-in-out_infinite]" />

                  <div className={`relative w-20 h-20 rounded-2xl p-3 transition-all duration-500 shadow-2xl ${
                    isNight ? 'bg-white/10' : 'bg-white'
                  }`}>
                    <div className="w-full h-full bg-[--clinqr-navy] rounded-lg flex items-center justify-center">
                      {profile.device === 'mobile' ? (
                        <Smartphone className="w-8 h-8 text-[--clinqr-lime]" strokeWidth={1.5} />
                      ) : (
                        <Monitor className="w-8 h-8 text-[--clinqr-lime]" strokeWidth={1.5} />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <p className={`text-center text-[10px] uppercase tracking-[0.2em] font-bold mb-8 transition-colors duration-500 ${isNight ? 'text-white/30' : 'text-[--clinqr-navy]/30'}`}>
                QR Scanned
              </p>

              {/* Rules */}
              <div className="space-y-3">
                {rules.map((rule, index) => {
                  const isMatched = index === matchedIndex;
                  const isAfterMatch = index > matchedIndex;

                  return (
                    <div
                      key={rule.id}
                      className={`relative flex items-center gap-4 p-5 rounded-2xl transition-all duration-500 ease-out border ${
                        isMatched
                          ? 'bg-[--clinqr-navy] text-[--clinqr-lime] shadow-2xl scale-[1.05] z-10 border-transparent'
                          : isAfterMatch
                          ? `bg-transparent opacity-40 ${isNight ? 'border-white/20' : 'border-[--clinqr-navy]/20'}`
                          : `bg-transparent ${isNight ? 'border-white/30' : 'border-[--clinqr-navy]/30'}`
                      }`}
                    >
                      {/* Index/Check */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
                        isMatched
                          ? 'bg-[--clinqr-lime] text-[--clinqr-navy]'
                          : isNight
                          ? 'bg-white/10 text-white/30'
                          : 'bg-[--clinqr-navy]/10 text-[--clinqr-navy]/30'
                      }`}>
                        {isMatched ? (
                          <Check className="w-5 h-5" strokeWidth={3} />
                        ) : (
                          <span className="text-xs font-bold">{index + 1}</span>
                        )}
                      </div>

                      {/* Label */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-bold truncate transition-colors duration-500 ${
                          isMatched
                            ? 'text-[--clinqr-lime]'
                            : isNight
                            ? 'text-white/60'
                            : 'text-[--clinqr-navy]/60'
                        }`}>
                          {rule.isFallback ? 'Default (Fallback)' : rule.label}
                        </p>
                      </div>

                      {/* Destination */}
                      <code className={`flex items-center gap-1.5 text-[10px] font-mono px-2 py-1 rounded-lg transition-all duration-500 ${
                        isMatched
                          ? 'bg-[--clinqr-lime]/10 text-[--clinqr-lime]'
                          : isNight
                          ? 'bg-white/5 text-white/30'
                          : 'bg-[--clinqr-navy]/5 text-[--clinqr-navy]/30'
                      }`}>
                        {rule.isFallback && <Instagram className="w-3 h-3" />}
                        {rule.isFallback ? '@thebistro' : rule.destination}
                      </code>
                    </div>
                  );
                })}
              </div>

              {/* Result */}
              <div className={`mt-10 p-6 rounded-2xl transition-all duration-500 text-center ${
                isNight
                  ? 'bg-[--clinqr-lime]/10 border border-[--clinqr-lime]/20'
                  : 'bg-[--clinqr-navy] border border-[--clinqr-navy] shadow-2xl'
              }`}>
                <p className={`text-[10px] uppercase tracking-widest mb-2 font-bold transition-colors duration-500 ${isNight ? 'text-[--clinqr-lime]' : 'text-white/40'}`}>
                  Redirects to:
                </p>
                <div className={`flex items-center justify-center gap-2 text-xl font-mono font-bold transition-colors duration-500 ${isNight ? 'text-white' : 'text-[--clinqr-lime]'}`}>
                  {matchedRule.isFallback && <Instagram className="w-5 h-5" />}
                  {matchedRule.isFallback ? '@thebistro' : matchedRule.destination}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Note */}
        <p className={`text-center text-xs mt-10 font-bold uppercase tracking-widest transition-colors duration-500 ${isNight ? 'text-white/20' : 'text-[--clinqr-navy]/20'}`}>
          Rules evaluated in order. First match wins.
        </p>
      </div>
    </section>
  );
}
